import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom'
import { ScrollToTop } from './components/ScrollToTop'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { AdminAuthProvider, useAdminAuth } from './hooks/useAdminAuth'
import { supabase } from './integrations/supabase/client'
import { useEffect, useState, useCallback, lazy, Suspense, Component, type ReactNode } from 'react'
import { AIStudyChat } from './components/AIStudyChat'
import { ProfileProvider, useProfile } from './hooks/useProfile'

type LazyModule<T extends React.ComponentType<unknown>> = { default: T }

// Retry wrapper for lazy imports — retries up to 3 times on chunk load failure
function lazyRetry<T extends React.ComponentType<unknown>>(importFn: () => Promise<LazyModule<T>>) {
  return lazy(() =>
    importFn().catch((err) => {
      console.warn('Chunk load failed, retrying...', err)
      return new Promise<void>((resolve) => setTimeout(resolve, 1500))
        .then(() => importFn())
        .catch((err2) => {
          console.warn('Chunk load retry 2...', err2)
          return new Promise<void>((resolve) => setTimeout(resolve, 2000)).then(() => importFn())
        })
        .catch(() => {
          // Final fallback: reload the page to get fresh chunks
          window.location.reload()
          return { default: ((() => null) as unknown) as T }
        })
    })
  )
}

// Lazy-loaded pages with retry
const LandingPage = lazyRetry(() => import('./pages/LandingPage'))
const AuthPage = lazyRetry(() => import('./pages/AuthPage'))
const OnboardingPage = lazyRetry(() => import('./pages/OnboardingPage'))
const QuizPage = lazyRetry(() => import('./pages/QuizPage'))
const DashboardPage = lazyRetry(() => import('./pages/DashboardPage'))
const CertificatePage = lazyRetry(() => import('./pages/CertificatePage'))
const CertificateReportPage = lazyRetry(() => import('./pages/CertificateReportPage'))
const HowItWorksPage = lazyRetry(() => import('./pages/HowItWorksPage'))
const FeaturesPage = lazyRetry(() => import('./pages/FeaturesPage'))
const PricingPage = lazyRetry(() => import('./pages/PricingPage'))
const BlogPage = lazyRetry(() => import('./pages/BlogPage'))
const BlogPostPage = lazyRetry(() => import('./pages/BlogPostPage'))
const PrivacyPolicyPage = lazyRetry(() => import('./pages/PrivacyPolicyPage'))
const TermsPage = lazyRetry(() => import('./pages/TermsPage'))
const RefundPolicyPage = lazyRetry(() => import('./pages/RefundPolicyPage'))
const ContactPage = lazyRetry(() => import('./pages/ContactPage'))
const CategoryPage = lazyRetry(() => import('./pages/CategoryPage'))
const PaywallPage = lazyRetry(() => import('./pages/PaywallPage'))
const NotFound = lazyRetry(() => import('./pages/NotFound'))
const SharedCertificatePage = lazyRetry(() => import('./pages/SharedCertificatePage'))
const ResetPasswordPage = lazyRetry(() => import('./pages/ResetPasswordPage'))
const VerifyEmailPage = lazyRetry(() => import('./pages/VerifyEmailPage'))
const SEOToolPage = lazyRetry(() => import('./pages/SEOToolPage'))

// Preload only the landing page after initial render (auth/dashboard loaded on demand)
if (typeof window !== 'undefined') {
  const preload = () => {
    import('./pages/LandingPage')
  }
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(preload)
  } else {
    setTimeout(preload, 2000)
  }
}

// Admin pages
const AdminLayout = lazyRetry(() => import('./components/admin/AdminLayout'))
const AdminLoginPage = lazyRetry(() => import('./pages/admin/AdminLoginPage'))
const AdminDashboardPage = lazyRetry(() => import('./pages/admin/AdminDashboardPage'))
const AdminUsersPage = lazyRetry(() => import('./pages/admin/AdminUsersPage'))
const AdminUserDetailPage = lazyRetry(() => import('./pages/admin/AdminUserDetailPage'))
const AdminUserPreviewPage = lazyRetry(() => import('./pages/admin/AdminUserPreviewPage'))
const AdminAdminsPage = lazyRetry(() => import('./pages/admin/AdminAdminsPage'))
const AdminAuditLogPage = lazyRetry(() => import('./pages/admin/AdminAuditLogPage'))
const AdminActivityPage = lazyRetry(() => import('./pages/admin/AdminActivityPage'))
const AdminFraudPage = lazyRetry(() => import('./pages/admin/AdminFraudPage'))
const AdminNotificationsPage = lazyRetry(() => import('./pages/admin/AdminNotificationsPage'))
const AdminPurchasesPage = lazyRetry(() => import('./pages/admin/AdminPurchasesPage'))
const AdminLessonsPage = lazyRetry(() => import('./pages/admin/AdminLessonsPage'))
const AdminCommunityPage = lazyRetry(() => import('./pages/admin/AdminCommunityPage'))
const AdminBlogPage = lazyRetry(() => import('./pages/admin/AdminBlogPage'))
const AdminEmailCampaignsPage = lazyRetry(() => import('./pages/admin/AdminEmailCampaignsPage'))

// Error boundary to catch chunk load failures
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }
  static getDerivedStateFromError() {
    return { hasError: true }
  }
  componentDidCatch(error: unknown) {
    console.error('Chunk error boundary caught:', error)
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
          <p className="text-foreground text-sm">Something went wrong loading the page.</p>
          <button onClick={() => window.location.reload()} className="text-primary text-sm underline">Reload</button>
        </div>
      )
    }
    return this.props.children
  }
}

// Ultra-fast loading fallback — barely visible for instant transitions
function PageLoader() {
  const [show, setShow] = useState(false)
  useEffect(() => {
    // Only show spinner after 300ms delay to avoid flash on fast loads
    const t = setTimeout(() => setShow(true), 300)
    return () => clearTimeout(t)
  }, [])
  if (!show) return <div className="min-h-screen bg-background" />
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

function ProtectedRoute({ children, requireQuiz = false, requirePayment = false }: { children: React.ReactNode; requireQuiz?: boolean; requirePayment?: boolean }) {
  const { user, loading: authLoading } = useAuth()
  const { profile, loading: profileLoading } = useProfile()
  const [searchParams] = useSearchParams()
  const isPaymentReturn = searchParams.get('payment') === 'success'

  // Determine access SYNCHRONOUSLY from cache first (5 min cache for quick loads)
  const cachedAccess = (() => {
    if (!requirePayment) return true
    const cached = localStorage.getItem('subscription_active')
    const cacheTime = localStorage.getItem('subscription_checked_at')
    const cacheAge = cacheTime ? Date.now() - parseInt(cacheTime) : Infinity
    if (cached === 'true' && !isPaymentReturn && cacheAge < 5 * 60 * 1000) return true
    return null // unknown — will check with backend
  })()

  const [hasAccess, setHasAccess] = useState<boolean | null>(cachedAccess)

  const checkSubscription = useCallback(async (retries = 0): Promise<boolean> => {
    if (isPaymentReturn) {
      localStorage.removeItem('subscription_active')
      localStorage.removeItem('subscription_checked_at')
    }

    try {
      const { data } = await supabase.functions.invoke('check-subscription')
      if (data?.subscribed === true) {
        localStorage.setItem('subscription_active', 'true')
        localStorage.setItem('subscription_checked_at', Date.now().toString())
        return true
      }
      if (isPaymentReturn && retries < 10) {
        await new Promise(r => setTimeout(r, 2500))
        return checkSubscription(retries + 1)
      }
      localStorage.removeItem('subscription_active')
      localStorage.removeItem('subscription_checked_at')
      return false
    } catch {
      const cached = localStorage.getItem('subscription_active')
      const cacheTime = localStorage.getItem('subscription_checked_at')
      const cacheAge = cacheTime ? Date.now() - parseInt(cacheTime) : Infinity
      if (cached === 'true' && cacheAge < 10 * 60 * 1000) return true
      if (isPaymentReturn && retries < 8) {
        await new Promise(r => setTimeout(r, 2500))
        return checkSubscription(retries + 1)
      }
      return false
    }
  }, [isPaymentReturn])

  useEffect(() => {
    if (!user) return

    if (!requirePayment) {
      setHasAccess(true)
      return
    }

    // If we already have cached access, verify in background only
    if (hasAccess === true && !isPaymentReturn) {
      checkSubscription().then(result => {
        if (!result) setHasAccess(false)
      })
      return
    }

    // No cache — need to check (but with a 5s hard timeout)
    const timeout = setTimeout(() => {
      // If still null after 5s, check cache one more time then deny
      setHasAccess(prev => {
        if (prev !== null) return prev
        const cached = localStorage.getItem('subscription_active')
        return cached === 'true' ? true : false
      })
    }, 5000)

    checkSubscription().then(result => {
      clearTimeout(timeout)
      setHasAccess(result)
    })

    return () => clearTimeout(timeout)
  }, [user, requirePayment, checkSubscription, hasAccess, isPaymentReturn])

  // Auth/profile still loading — show brief loader (max ~1-2s typically)
  if (authLoading || profileLoading) return <PageLoader />
  if (!user) return <Navigate to="/login" replace />
  if (!profile?.onboarding_completed) return <Navigate to="/start" replace />
  if (requireQuiz && !profile?.quiz_completed && hasAccess !== true) return <Navigate to={`/test/${profile?.category || 'nail-technician'}`} replace />
  
  // If we have cached access, render immediately (don't wait for API)
  if (hasAccess === true) return <>{children}</>
  // If access explicitly denied, redirect
  if (hasAccess === false && requirePayment) return <Navigate to={`/plans/${profile?.category || 'nail-technician'}`} replace />
  // Still checking (no cache) — show loader with 5s max timeout
  if (hasAccess === null) return <PageLoader />
  return <>{children}</>
}

function PaywallGuard({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const { profile, loading: profileLoading } = useProfile()
  const [hasAccess, setHasAccess] = useState<boolean>(false)

  const cachedProfile: { onboarding_completed?: boolean; quiz_completed?: boolean } | null = (() => {
    if (!user) return null
    try {
      const raw = localStorage.getItem(`profile_${user.id}`)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })()

  const hasOnboardingAccess = Boolean(
    profile?.onboarding_completed || cachedProfile?.onboarding_completed || cachedProfile?.quiz_completed
  )

  useEffect(() => {
    if (!user) { setHasAccess(false); return }

    const cached = localStorage.getItem('subscription_active')
    const cacheTime = localStorage.getItem('subscription_checked_at')
    const cacheAge = cacheTime ? Date.now() - parseInt(cacheTime) : Infinity

    // If user has a recent valid subscription cache, redirect to dashboard
    if (cached === 'true' && cacheAge < 5 * 60 * 1000) {
      setHasAccess(true)
      return
    }

    // No recent subscription cache → show paywall INSTANTLY (don't wait for API)
    // This prevents the long loading screen after completing a test
    setHasAccess(false)

    // Fire-and-forget: check in background in case they DO have access
    supabase.functions.invoke('check-subscription').then(({ data }) => {
      const subscribed = data?.subscribed === true
      if (subscribed) {
        localStorage.setItem('subscription_active', 'true')
        localStorage.setItem('subscription_checked_at', Date.now().toString())
        setHasAccess(true)
      }
    }).catch(() => {})
  }, [user])

  // Never show a loader — always render paywall or redirect
  if (!user && !authLoading) return <Navigate to="/login" replace />
  if (!user) return <>{children}</>
  if (hasAccess) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const { loading: profileLoading } = useProfile()

  if (authLoading || profileLoading) return <PageLoader />
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function AuthRedirect({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const { profile, loading: profileLoading } = useProfile()

  // Always render children (AuthPage with SEO tags) so crawlers see content
  // Only redirect authenticated users after loading completes
  if (authLoading || profileLoading) return <>{children}</>
  if (user && profile?.quiz_completed) return <Navigate to="/dashboard" replace />
  if (user && profile?.onboarding_completed) return <Navigate to="/start" replace />
  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const { isAdmin, loading } = useAdminAuth()

  if (authLoading || loading) return <div className="min-h-screen bg-[#0f1117] flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
  if (!user || !isAdmin) return <Navigate to="/admin/login" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProfileProvider>
        <AdminAuthProvider>
          <ScrollToTop />
          <ChunkErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<AuthRedirect><AuthPage /></AuthRedirect>} />
                <Route path="/signup" element={<AuthRedirect><AuthPage /></AuthRedirect>} />
                {/* Legacy /auth redirect */}
                <Route path="/auth" element={<AuthRedirect><AuthPage /></AuthRedirect>} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/start" element={<OnboardingGuard><OnboardingPage /></OnboardingGuard>} />
                {/* Legacy /onboarding redirect */}
                <Route path="/onboarding" element={<Navigate to="/start" replace />} />
                <Route path="/test/:category" element={<OnboardingGuard><QuizPage /></OnboardingGuard>} />
                {/* Legacy /quiz redirect */}
                <Route path="/quiz/:category" element={<OnboardingGuard><QuizPage /></OnboardingGuard>} />
                <Route path="/dashboard" element={<ProtectedRoute requireQuiz requirePayment><DashboardPage /></ProtectedRoute>} />
                <Route path="/cert" element={<ProtectedRoute requireQuiz><CertificatePage /></ProtectedRoute>} />
                {/* Legacy /certificate redirect */}
                <Route path="/certificate" element={<Navigate to="/cert" replace />} />
                <Route path="/report" element={<ProtectedRoute requireQuiz><CertificateReportPage /></ProtectedRoute>} />
                {/* Legacy /certificate-report redirect */}
                <Route path="/certificate-report" element={<Navigate to="/report" replace />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/refund-policy" element={<RefundPolicyPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/category/:slug" element={<CategoryPage />} />
                <Route path="/plans/:category" element={<PaywallGuard><PaywallPage /></PaywallGuard>} />
                <Route path="/plans" element={<PaywallGuard><PaywallPage /></PaywallGuard>} />
                {/* Legacy /paywall redirect */}
                <Route path="/paywall/:category" element={<PaywallGuard><PaywallPage /></PaywallGuard>} />
                <Route path="/paywall" element={<PaywallGuard><PaywallPage /></PaywallGuard>} />
                <Route path="/c/:id" element={<SharedCertificatePage />} />
                <Route path="/tools/seo-checker" element={<SEOToolPage />} />
                {/* Admin */}
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                  <Route index element={<AdminDashboardPage />} />
                  <Route path="users" element={<AdminUsersPage />} />
                  <Route path="users/:userId" element={<AdminUserDetailPage />} />
                  <Route path="users/:userId/preview" element={<AdminUserPreviewPage />} />
                  <Route path="fraud" element={<AdminFraudPage />} />
                  <Route path="purchases" element={<AdminPurchasesPage />} />
                  <Route path="lessons" element={<AdminLessonsPage />} />
                  <Route path="community" element={<AdminCommunityPage />} />
                  <Route path="notifications" element={<AdminNotificationsPage />} />
                  <Route path="blog" element={<AdminBlogPage />} />
                  <Route path="email-campaigns" element={<AdminEmailCampaignsPage />} />
                  <Route path="activity" element={<AdminActivityPage />} />
                  
                  <Route path="admins" element={<AdminAdminsPage />} />
                  <Route path="audit-log" element={<AdminAuditLogPage />} />
                </Route>

                {/* Catch-all 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ChunkErrorBoundary>
          <AIStudyChat />
        </AdminAuthProvider>
        </ProfileProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}